// ────────────────────────────────────────────────────────────────────
// The three cards under the components table (design_handoff/README.md:206-208):
// OPEN RECOMMENDATIONS ON THIS ASSET · t-min IS SET HERE, ONCE ·
// REPAIRS, ALTERATIONS & RE-RATINGS. They wrap at 300px.
//
// TWO OF THE THREE HAVE NO SOURCE. `AssetRecommendation` and `AssetRepair`
// (server/models.py:1121, :1108) have no reader, no writer and no route, so
// neither is on the integrity record and neither can be. They render the
// honest empty state from ./emptyStates — the 2026-08-31 ruling — and they
// take their rows from a prop so that the day a writer exists, the row
// rendering and its pills are already here and tested.
//
// THE t-min CARD PRINTS WHAT THE RECORD HOLDS AND NOT ONE WORD MORE.
// `governing_t_min_mm` and `governing_component` are on the record. The
// handoff's card also carries a basis ("API 653 §4.4.7.1b"), a paragraph of
// history and a rule line ("changed twice since 2004 · both changes signed") —
// all authored per asset in the prototype (XZYNEXA.dc.html:2577), none of it
// anywhere in the tree. Two reasons it is not reconstructed here:
//
//   1. It would be a second authority on where a number came from, which is
//      the defect that moved all four rate sentences into
//      server/integrity/rules.py in task 4b — and moving them revealed that
//      the browser's version had been naming the wrong component all along.
//   2. It would be a clause number. `rule_ref` is blank on every candidate
//      until a qualified inspector verifies the sections (the 2026-08-27
//      ruling), because a wrong clause number in a compliance product reads
//      as authority.
//
// So the card says the basis is not on the record. That sentence is the
// finding, and it belongs on screen rather than in a commit message.
// ────────────────────────────────────────────────────────────────────
import type { CSSProperties, ReactNode } from "react";
import { RECOMMENDATIONS_NOT_CAPTURED, REPAIRS_NOT_CAPTURED } from "./emptyStates";
import { mm } from "./format";
import type { AssetIntegrity } from "./types";

/** One row of `asset_repairs`, in the model's own shape (models.py:1108).
 *  `kind` is `repair | alteration | re_rating | other`. */
export interface AssetRepairRow {
  kind: string;
  performed_on: string;
  summary: string;
  reference: string;
}

/** One row of `asset_recommendations` (models.py:1121). */
export interface AssetRecommendationRow {
  priority: string;
  status: string;
  due: string;
  summary: string;
}

const MICRO: CSSProperties = {
  fontSize: 8.5, fontWeight: 500, letterSpacing: ".14em",
  color: "var(--ink-4)", marginBottom: 9,
};
const EMPTY: CSSProperties = { fontSize: 11.5, lineHeight: 1.6, color: "var(--ink-4)" };
const PILL: CSSProperties = {
  fontSize: 8, fontWeight: 600, letterSpacing: ".1em", padding: "3px 7px",
  borderRadius: 20, flex: "none", marginTop: 1, whiteSpace: "nowrap",
};

function Card({ title, tinted = false, testId, children }: {
  title: string; tinted?: boolean; testId?: string; children: ReactNode;
}): JSX.Element {
  return (
    <div data-testid={testId}
         style={{
           flex: 1, minWidth: 300, border: "1px solid var(--hairline)", borderRadius: 10,
           background: tinted ? "var(--surface-warm)" : "var(--paper-2)", padding: "14px 16px",
         }}>
      <div className="font-mono" style={MICRO}>{title}</div>
      {children}
    </div>
  );
}

/** repair · alteration · re-rating · anything else. An alteration or a
 *  re-rating changed what the asset is rated for and a repair did not, which
 *  is why the handoff gives them different pills rather than one "work done"
 *  row. Unknown kinds fall to `other` rather than being printed raw: the
 *  column is a controlled vocabulary and a row outside it is still a row. */
function repairKind(kind: string): { key: string; label: string; style: CSSProperties } {
  const k = kind.toLowerCase();
  if (k === "repair") {
    return { key: "repair", label: "REPAIR",
             style: { background: "rgba(var(--accent-rgb) / 0.12)", color: "var(--accent-deep)" } };
  }
  if (k === "alteration" || k === "re_rating" || k === "re-rating") {
    return { key: k === "alteration" ? "alteration" : "re_rating",
             label: k === "alteration" ? "ALTERATION" : "RE-RATING",
             style: { background: "color-mix(in srgb, var(--violet) 12%, var(--paper-2))",
                      color: "var(--violet)" } };
  }
  return { key: "other", label: "OTHER",
           style: { background: "var(--line-2)", color: "var(--ink-3)" } };
}

function RepairsCard({ repairs }: { repairs: readonly AssetRepairRow[] }): JSX.Element {
  return (
    <Card title="REPAIRS, ALTERATIONS & RE-RATINGS · THE METAL CHANGED">
      {repairs.map((r, i) => {
        const kind = repairKind(r.kind);
        return (
          <div key={`${r.reference}-${i}`} data-testid="repair-row" data-kind={kind.key}
               style={{ display: "flex", alignItems: "flex-start", gap: 10,
                        padding: "8px 0", borderBottom: "1px solid var(--line-3)" }}>
            <span className="font-mono" style={{ ...PILL, ...kind.style }}>{kind.label}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, lineHeight: 1.45, color: "var(--ink-1)" }}>{r.summary}</div>
              <div className="font-mono" style={{ fontSize: 9.5, color: "var(--ink-4)", marginTop: 2 }}>
                {r.reference}
              </div>
            </div>
            <span className="font-mono"
                  style={{ fontSize: 10.5, fontWeight: 500, color: "var(--ink-3)", flex: "none" }}>
              {r.performed_on}
            </span>
          </div>
        );
      })}
      {repairs.length === 0 && (
        <p data-testid="repairs-empty" style={EMPTY}>{REPAIRS_NOT_CAPTURED}</p>
      )}
    </Card>
  );
}

function RecommendationsCard({ recommendations }: {
  recommendations: readonly AssetRecommendationRow[];
}): JSX.Element {
  return (
    <Card title="OPEN RECOMMENDATIONS ON THIS ASSET">
      {recommendations.map((r, i) => (
        <div key={`${r.summary}-${i}`} data-testid="recommendation-row"
             data-priority={r.priority || "none"}
             style={{ display: "flex", alignItems: "flex-start", gap: 10,
                      padding: "8px 0", borderBottom: "1px solid var(--line-3)" }}>
          <span className="font-mono"
                style={{ ...PILL, background: "rgba(var(--accent-rgb) / 0.12)",
                         color: "var(--accent-deep)" }}>
            {(r.priority || r.status || "OPEN").toUpperCase()}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, lineHeight: 1.45, color: "var(--ink-1)" }}>{r.summary}</div>
          </div>
          <span className="font-mono"
                style={{ fontSize: 10.5, fontWeight: 500, color: "var(--ink-2)", flex: "none" }}>
            {r.due}
          </span>
        </div>
      ))}
      {recommendations.length === 0 && (
        <p data-testid="recommendations-empty" style={EMPTY}>{RECOMMENDATIONS_NOT_CAPTURED}</p>
      )}
    </Card>
  );
}

function TminCard({ record }: { record: AssetIntegrity }): JSX.Element {
  const held = record.governing_component;
  const n = record.components.length;
  return (
    <Card title="t-min IS SET HERE, ONCE" tinted testId="tmin-card">
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 7 }}>
        <span data-testid="tmin-value" className="font-mono"
              style={{ fontSize: 20, fontWeight: 600, color: "var(--ink-1)" }}>
          {mm(record.governing_t_min_mm)}
        </span>
        <span data-testid="tmin-held-on" className="font-mono"
              style={{ fontSize: 10, color: "var(--ink-4)" }}>
          {held ? `mm · held against ${held}` : "mm · no component named on the record"}
        </span>
      </div>

      <div style={{ fontSize: 11.5, lineHeight: 1.65, color: "var(--ink-3)" }}>
        This is the minimum every figure on this screen is measured down to: the remaining life,
        the interval and the date all run from the last reading to this number. It is held on the
        asset, not typed into a job — a job reads it, and changing it here changes what every open
        assessment was judged against.
      </div>

      <p data-testid="tmin-basis-note" style={{ ...EMPTY, marginTop: 8 }}>
        How this minimum was arrived at — the calculation behind it, and who changed it and when —
        is not on the asset record. It is not reconstructed here: a basis composed in the browser
        would be a second authority on where a number came from, and the one thing worse than an
        unexplained minimum is a confidently wrong explanation of it.
      </p>

      <div className="font-mono"
           style={{ marginTop: 10, fontSize: 10, fontWeight: 500, color: "var(--accent-deep)" }}>
        {n > 1
          ? `THE GOVERNING MINIMUM OF ${n} COMPONENTS ON FILE`
          : "ONE MINIMUM ON FILE FOR THIS ASSET"}
      </div>
    </Card>
  );
}

export function AssetBottomCards({ record, recommendations = [], repairs = [] }: {
  record: AssetIntegrity;
  /** No producer exists — `AssetRecommendation` has no route. Here so that the
   *  row rendering is written and tested before the writer is. */
  recommendations?: readonly AssetRecommendationRow[];
  repairs?: readonly AssetRepairRow[];
}): JSX.Element {
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start",
                  flexWrap: "wrap", marginTop: 16 }}>
      <RecommendationsCard recommendations={recommendations} />
      <TminCard record={record} />
      <RepairsCard repairs={repairs} />
    </div>
  );
}
